import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ApiV1Service } from './api.v1.service';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  constructor(private api: ApiService, private apiV1: ApiV1Service) {}

  // ── Games ─────────────────────────────────────────────────────────────────

  async getGames(
    items_per_page: number | null = null,
    type: string | null = null,
    language: string | null = null
  ) {
    let query = `/game?${items_per_page ? `items_per_page=${items_per_page}` : ''}${type ? `&type=${type}` : ''}${language ? `&language=${language}` : ''}`;
    return await this.apiV1.get(query);
  }

  async getGame(id: number) {
    return await this.apiV1.get(`/game/${id}`);
  }

  async createGame(data: any) {
    return await this.apiV1.postJson(`/game`, data);
  }

  async updateGame(id: number, payload: any, files?: Record<string, File>) {
    const fd = new FormData();
    fd.append('_method', 'PUT');
    fd.append('identifier', payload.identifier);
    fd.append('type', payload.type);
    if (payload.price !== null && payload.price !== undefined) fd.append('price', String(payload.price));
    fd.append('free_sample', payload.free_sample ? '1' : '0');
    fd.append('active', payload.active ? '1' : '0');
    fd.append('data_json', JSON.stringify(payload.data_json));
    fd.append('sliders_json', JSON.stringify(payload.sliders_json || {}));
    if (files) {
      Object.entries(files).forEach(([key, file]) => fd.append(key, file));
    }
    return await this.apiV1.postFile(`/game/${id}`, fd);
  }

  async deleteGame(id: number) {
    return await this.apiV1.delete(`/game/${id}`);
  }

  async activateGame(id: number) {
    try {
      return await this.apiV1.patch(`/game/${id}/activate`, {});
    } catch (error) {
      return error;
    }
  }

  async deactivateGame(id: number) {
    try {
      return await this.apiV1.patch(`/game/${id}/deactivate`, {});
    } catch (error) {
      return error;
    }
  }

  // ── Fields ───────────────────────────────────────────────────────────────

  async getFields(active: boolean | null = null) {
    const query = active !== null ? `/field?active=${active}` : `/field`;
    return await this.apiV1.get(query);
  }

  // ── Themes ────────────────────────────────────────────────────────────────
  // GET  /api/v1/theme?game_id=X&field_id=Y
  // POST /api/v1/theme                       (multipart/form-data)
  // GET  /api/v1/theme/{id}
  // POST /api/v1/theme/{id}  _method=PUT     (multipart/form-data)
  // DELETE /api/v1/theme/{id}

  async getThemes(gameId: number, fieldId: number) {
    return await this.apiV1.get(`/theme?game_id=${gameId}&field_id=${fieldId}`);
  }

  async getGridThemes(gameId: number) {
    return await this.apiV1.get(`/theme?game_id=${gameId}`);
  }

  async createTheme(payload: {
    game_id: number;
    field_id?: number | null;
    identifier: string;
    order: number;
    data_json: any;
    free?: boolean;
    image?: File | null;
    mp3?: File | null;
  }) {
    try {
      const fd = new FormData();
      fd.append('game_id', String(payload.game_id));
      if (payload.field_id != null) fd.append('field_id', String(payload.field_id));
      fd.append('identifier', payload.identifier);
      fd.append('order', String(payload.order));
      fd.append('data_json', JSON.stringify(payload.data_json));
      fd.append('free', payload.free ? '1' : '0');
      if (payload.image) fd.append('image', payload.image);
      if (payload.mp3) fd.append('mp3', payload.mp3);
      return await this.apiV1.postFile(`/theme`, fd);
    } catch (error) {
      throw error;
    }
  }

  async updateTheme(themeId: number, payload: {
    game_id: number;
    field_id?: number | null;
    identifier: string;
    order: number;
    data_json: any;
    free?: boolean;
    image?: File | null;
    mp3?: File | null;
  }) {
    try {
      const fd = new FormData();
      fd.append('_method', 'PUT');
      fd.append('game_id', String(payload.game_id));
      if (payload.field_id != null) fd.append('field_id', String(payload.field_id));
      fd.append('identifier', payload.identifier);
      fd.append('order', String(payload.order));
      fd.append('data_json', JSON.stringify(payload.data_json));
      fd.append('free', payload.free ? '1' : '0');
      if (payload.image) fd.append('image', payload.image);
      if (payload.mp3) fd.append('mp3', payload.mp3);
      return await this.apiV1.postFile(`/theme/${themeId}`, fd);
    } catch (error) {
      throw error;
    }
  }

  async saveThemeArchetypes(
    themeId: number, gameId: number, identifier: string, order: number,
    fieldId: number | null | undefined, data_json: any,
    lightImages: (File | null)[], shadowImages: (File | null)[],
    free?: boolean
  ) {
    const fd = new FormData();
    fd.append('_method', 'PUT');
    fd.append('game_id', String(gameId));
    if (fieldId != null) fd.append('field_id', String(fieldId));
    fd.append('identifier', identifier);
    fd.append('order', String(order));
    fd.append('data_json', JSON.stringify(data_json));
    fd.append('free', free ? '1' : '0');
    lightImages.forEach((f, i) => { if (f) fd.append(`light_image_${i}`, f); });
    shadowImages.forEach((f, i) => { if (f) fd.append(`shadow_image_${i}`, f); });
    return await this.apiV1.postFile(`/theme/${themeId}`, fd);
  }

  async deleteTheme(themeId: number) {
    try {
      return await this.apiV1.delete(`/theme/${themeId}`);
    } catch (error) {
      throw error;
    }
  }

  // ── Cards ─────────────────────────────────────────────────────────────────
  // GET    /api/v1/card?theme_id=X
  // POST   /api/v1/card                    (multipart/form-data)
  // POST   /api/v1/card/{id}  _method=PUT  (multipart/form-data)
  // DELETE /api/v1/card/{id}
  //
  // data_json per-language keys: name (required), type (required: light|shadow),
  // title, subtitle, image, icon, text_color, background_color, card_text, bitcoin_text, mp3
  // Base64 in data_json.image/icon/mp3 is auto-converted to storage files by the API.

  async getCards(themeId: number) {
    return await this.apiV1.get(`/card?theme_id=${themeId}`);
  }

  async createCard(payload: {
    theme_id: number;
    identifier: string;
    order: number;
    data_json: any;
    image?: File | null;
    icon?: File | null;
    mp3?: File | null;
  }) {
    try {
      const fd = new FormData();
      fd.append('theme_id', String(payload.theme_id));
      fd.append('identifier', payload.identifier);
      fd.append('order', String(payload.order));
      fd.append('data_json', JSON.stringify(payload.data_json));
      if (payload.image) fd.append('image', payload.image);
      if (payload.icon) fd.append('icon', payload.icon);
      if (payload.mp3) fd.append('mp3', payload.mp3);
      return await this.apiV1.postFile(`/card`, fd);
    } catch (error) {
      throw error;
    }
  }

  async updateCard(cardId: number, payload: {
    theme_id: number;
    identifier: string;
    order: number;
    data_json: any;
    image?: File | null;
    icon?: File | null;
    mp3?: File | null;
  }) {
    try {
      const fd = new FormData();
      fd.append('_method', 'PUT');
      fd.append('theme_id', String(payload.theme_id));
      fd.append('identifier', payload.identifier);
      fd.append('order', String(payload.order));
      fd.append('data_json', JSON.stringify(payload.data_json));
      if (payload.image) fd.append('image', payload.image);
      if (payload.icon) fd.append('icon', payload.icon);
      if (payload.mp3) fd.append('mp3', payload.mp3);
      return await this.apiV1.postFile(`/card/${cardId}`, fd);
    } catch (error) {
      throw error;
    }
  }

  async deleteCard(cardId: number) {
    try {
      return await this.apiV1.delete(`/card/${cardId}`);
    } catch (error) {
      throw error;
    }
  }

  // ── App Config ────────────────────────────────────────────────────────────

  async getAppConfig() {
    try {
      return await this.apiV1.get(`/app-config`);
    } catch (error) {
      throw error;
    }
  }

  async updateAppConfig(payload: { how_to_play?: string | null; initial_sliders?: any }) {
    try {
      return await this.apiV1.put(`/app-config`, payload);
    } catch (error) {
      throw error;
    }
  }
}
